import { defineStore } from "pinia";
import { supabase, edgeUrl } from "@/lib/supabase";
import { resolveTenantById } from "@/lib/tenant";
import { applyBranding } from "@/lib/branding";
import { useTenantStore } from "@/stores/tenant";
import type { User } from "@supabase/supabase-js";

export const useAuthStore = defineStore("auth", {
  state: () => ({
    user: null as User | null,
    loading: true,
    initialized: false,
  }),
  getters: {
    isAuthenticated: (s) => !!s.user,
    email: (s) => s.user?.email ?? "",
    nome: (s) =>
      (s.user?.user_metadata as any)?.nome_completo ??
      (s.user?.user_metadata as any)?.nome ??
      s.user?.email?.split("@")[0] ??
      "Usuário",
    avatarUrl: (s) => (s.user?.user_metadata as any)?.avatar_url ?? null,
    tenantId: (s) => (s.user?.app_metadata as any)?.tenant_id ?? null,
    role: (s) => (s.user?.app_metadata as any)?.role ?? "viewer",
    isSuperAdmin: (s) => (s.user?.app_metadata as any)?.is_super_admin === true,
    isSindico: (s) => (s.user?.app_metadata as any)?.role === "sindico",
    isMorador: (s) => (s.user?.app_metadata as any)?.role === "morador",
    // App do papel de cliente (síndico/morador NÃO acessam o admin). null = staff/admin.
    appHome(): string | null {
      const r = (this.user?.app_metadata as any)?.role;
      return r === "sindico" ? "/sindico" : r === "morador" ? "/morador" : null;
    },
  },
  actions: {
    async init() {
      if (this.initialized) return;
      this.loading = true;
      const { data } = await supabase.auth.getSession();
      this.user = data.session?.user ?? null;
      this.loading = false;
      this.initialized = true;

      if (this.user) await this.ensureTenant();

      supabase.auth.onAuthStateChange(async (event, session) => {
        this.user = session?.user ?? null;
        if (event === "SIGNED_IN" && this.user) {
          if (!this.tenantId) await this.refreshTenantClaim();
          await this.ensureTenant();
        }
      });
    },

    async login(email: string, password: string) {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      await this.refreshTenantClaim();
      await this.ensureTenant();
    },

    async loginWithMagicLink(email: string) {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: `${location.origin}/dashboard` },
      });
      if (error) throw error;
    },

    /**
     * Garante que o tenant store esteja populado com o tenant real do usuário.
     * Em hosts admin (admin.aferi.app) o subdomínio não dá o slug, então
     * resolvemos pelo claim `app_metadata.tenant_id`, agora que o usuário
     * está autenticado, a RLS de `tenants` permite a leitura.
     */
    async ensureTenant() {
      const tenantStore = useTenantStore();
      if (tenantStore.id) return; // já resolvido pelo subdomínio
      const tid = this.tenantId;
      if (!tid) return;
      try {
        const tenant = await resolveTenantById(tid);
        if (tenant) {
          tenantStore.set(tenant);
          applyBranding(tenant);
        }
      } catch (e) {
        console.warn("ensureTenant failed:", e);
      }
    },

    /**
     * Chama a edge function set-tenant-claim para popular app_metadata.tenant_id no JWT.
     * Depois força refresh da session para o novo claim entrar em uso (RLS funciona).
     */
    async refreshTenantClaim() {
      try {
        const { data } = await supabase.auth.getSession();
        const token = data.session?.access_token;
        if (!token) return;

        // Se o JWT já traz o claim, não precisa bater na edge function.
        if (this.tenantId) return;

        const host = location.hostname;
        const slug = host.endsWith(".aferi.app") ? host.replace(/\.aferi\.app$/, "") : null;

        await fetch(edgeUrl("set-tenant-claim"), {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${token}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
            "Content-Type": "application/json",
            ...(slug ? { "x-tenant-slug": slug } : {}),
          },
          body: JSON.stringify(slug ? { tenant_slug: slug } : {}),
        });

        const { data: refreshed } = await supabase.auth.refreshSession();
        if (refreshed.user) this.user = refreshed.user;
      } catch (e) {
        console.warn("refreshTenantClaim failed:", e);
      }
    },

    async logout() {
      await supabase.auth.signOut();
      this.user = null;
      useTenantStore().$reset?.();
      location.href = "/login";
    },
  },
});
